/**
 * Frame profiler. The gate is p99, not median — see ARCHITECTURE.md §9.
 *
 * Records total frame time plus per-subsystem cost, and snapshots renderer counters.
 * Uses performance.now() — that is legal here because the profiler is NOT simulation code
 * and never feeds back into the sim.
 */

import { percentile } from './mathx.js';

const CAP = 4096;

export class Profiler {
  constructor(renderer) {
    this.renderer = renderer || null;
    this.enabled = true;

    this._frames = new Float64Array(CAP);
    this._frameCount = 0;
    this._head = 0;

    /** name -> { total, samples:Float64Array, head, count } */
    this._sections = new Map();
    this._openName = null;
    this._openStart = 0;

    this._frameStart = 0;
    this.lastFrameMs = 0;

    /** Long tasks (>50ms) observed after warmup. */
    this.longTasks = 0;
    this.warmupFrames = 60;

    this.drawCalls = 0;
    this.triangles = 0;
    this.programs = 0;
    this.textures = 0;
    this.geometries = 0;

    this._heapStart = this._heap();
    this.heapPeak = this._heapStart;
  }

  _heap() {
    return (performance.memory && performance.memory.usedJSHeapSize) || 0;
  }

  beginFrame() {
    if (!this.enabled) return;
    this._frameStart = performance.now();
    for (const s of this._sections.values()) s.frameTotal = 0;
  }

  endFrame() {
    if (!this.enabled) return;
    const ms = performance.now() - this._frameStart;
    this.lastFrameMs = ms;
    this._frames[this._head] = ms;
    this._head = (this._head + 1) % CAP;
    if (this._frameCount < CAP) this._frameCount++;

    if (this._totalFrames === undefined) this._totalFrames = 0;
    this._totalFrames++;
    if (ms > 50 && this._totalFrames > this.warmupFrames) this.longTasks++;

    for (const s of this._sections.values()) {
      s.samples[s.head] = s.frameTotal;
      s.head = (s.head + 1) % CAP;
      if (s.count < CAP) s.count++;
    }

    const h = this._heap();
    if (h > this.heapPeak) this.heapPeak = h;

    if (this.renderer && this.renderer.info) {
      const info = this.renderer.info;
      this.drawCalls = info.render.calls;
      this.triangles = info.render.triangles;
      this.programs = info.programs ? info.programs.length : 0;
      this.textures = info.memory.textures;
      this.geometries = info.memory.geometries;
    }
  }

  /** Wrap a subsystem call. Nestable only one level deep — keep it flat. */
  begin(name) {
    if (!this.enabled) return;
    let s = this._sections.get(name);
    if (!s) {
      s = { samples: new Float64Array(CAP), head: 0, count: 0, frameTotal: 0 };
      this._sections.set(name, s);
    }
    this._openName = name;
    this._openStart = performance.now();
  }

  end() {
    if (!this.enabled || !this._openName) return;
    const s = this._sections.get(this._openName);
    if (s) s.frameTotal += performance.now() - this._openStart;
    this._openName = null;
  }

  /** Convenience: profiler.wrap('vehicle', () => sys.fixedUpdate(dt)) */
  wrap(name, fn) {
    if (!this.enabled) return fn();
    this.begin(name);
    const r = fn();
    this.end();
    return r;
  }

  _sorted() {
    const n = this._frameCount;
    const out = new Float64Array(n);
    for (let i = 0; i < n; i++) out[i] = this._frames[i];
    return Array.prototype.slice.call(out).sort((a, b) => a - b);
  }

  /** Discard the first `n` recorded frames — used to drop warmup/compile spikes. */
  reset() {
    this._frameCount = 0; this._head = 0; this._totalFrames = 0;
    this.longTasks = 0;
    for (const s of this._sections.values()) { s.head = 0; s.count = 0; }
    this._heapStart = this._heap();
    this.heapPeak = this._heapStart;
  }

  sections() {
    const out = {};
    for (const [name, s] of this._sections) {
      const arr = Array.prototype.slice.call(s.samples.slice(0, s.count)).sort((a, b) => a - b);
      out[name] = {
        p50: +percentile(arr, 0.5).toFixed(3),
        p95: +percentile(arr, 0.95).toFixed(3),
        p99: +percentile(arr, 0.99).toFixed(3),
        max: arr.length ? +arr[arr.length - 1].toFixed(3) : 0,
      };
    }
    return out;
  }

  report() {
    const arr = this._sorted();
    const sum = arr.reduce((a, b) => a + b, 0);
    return {
      frames: arr.length,
      mean: arr.length ? +(sum / arr.length).toFixed(3) : 0,
      p50: +percentile(arr, 0.5).toFixed(3),
      p95: +percentile(arr, 0.95).toFixed(3),
      p99: +percentile(arr, 0.99).toFixed(3),
      max: arr.length ? +arr[arr.length - 1].toFixed(3) : 0,
      fpsFromP50: arr.length ? +(1000 / percentile(arr, 0.5)).toFixed(1) : 0,
      longTasks: this.longTasks,
      drawCalls: this.drawCalls,
      triangles: this.triangles,
      programs: this.programs,
      textures: this.textures,
      geometries: this.geometries,
      heapStartMB: +(this._heapStart / 1048576).toFixed(2),
      heapPeakMB: +(this.heapPeak / 1048576).toFixed(2),
      heapGrowthMB: +((this.heapPeak - this._heapStart) / 1048576).toFixed(2),
      sections: this.sections(),
    };
  }

  /** Raw frame times, oldest→newest, for histogram plots. */
  rawFrames() {
    const n = this._frameCount;
    const out = new Array(n);
    for (let i = 0; i < n; i++) out[i] = +this._frames[(this._head - n + i + CAP) % CAP].toFixed(3);
    return out;
  }
}
